// app/src/features/dev/useDebugWiring.js
import { useState } from "react";
import { useDebugOverrides } from "../../hooks/useDebugOverrides.js";

/**
 * The debug wiring: the ten debug-only `useState`s `AppInner` declared (the pipeline-debug mode,
 * the "today" override, the alert-badge force, the pipeline-debug strip's open flag and the GCP /
 * LLM probe state pairs) and `useDebugOverrides` (the forced verdict / premise / generating view
 * states), registered here in their old relative order. Called where `AppInner` declared
 * `debugMode` — after `useTrackingSettingsWiring`, before `useForecastAnimation`; the
 * `comparison` memo that sat between `mockToday` and the overrides now follows the call (the PR's
 * hook-order note). No inputs: every initialiser is a literal and the overrides hook takes none.
 *
 * Everything returned is read by `AppInner` under the same names: every state and setter RAW
 * (the setters are the `useState` setters themselves — `useSubmitWiring` takes
 * `setDebugPanelOpen` as an input and `SettingsModal` reads nearly all of the names through
 * `settingsCtx`), `mockToday` and the overrides as the staying hooks' arguments, and the strip's
 * reads (`debugPanelOpen`, `setDebugPanelOpen`) plus the three existing debug keys as `frameCtx`
 * keys for `DebugHost` and the frame. Nothing here reads the store or runs an effect. The GCP /
 * LLM probe HANDLERS are `SettingsModal`'s; this hook only owns their state.
 */
export function useDebugWiring() {
  const [debugMode, setDebugMode] = useState(false);
  // Debug "Set today's date & time" override. Empty by default so it's OPT-IN:
  // while empty, every consumer (`mockToday ? new Date(mockToday) : new Date()`)
  // uses LIVE wall-clock. Pre-filling it with page-load time would silently
  // freeze "today" for the whole session — reintroducing the REFORECAST cooldown
  // lockout and other time-based gating bugs. The picker sets it when the user
  // explicitly engages the override; clearing the field returns to live time.
  const [mockToday, setMockToday] = useState("");
  // Debug-only forced view states (resolution verdict and branch premise), both view-layer only.
  // The "unresolved" verdict also bypasses the resolution-date-in-past gate.
  const {
    resolution: debugResolutionOverride,
    setResolution: setDebugResolutionOverride,
    premise: debugPremiseOverride,
    setPremise: setDebugPremiseOverride,
    generating: debugForceGenerating,
    setGenerating: setDebugForceGenerating,
    clearAll: clearDebugOverrides,
  } = useDebugOverrides();
  // Forces the Forecast Log rail alert badge to show (with a representative count) regardless of
  // real f.alert flags or the forecastTracking gate. Purely visual — mutates no forecast.
  const [debugForceAlertBadge, setDebugForceAlertBadge] = useState(false);
  const [debugPanelOpen, setDebugPanelOpen] = useState(false);
  const [gcpResponse, setGcpResponse] = useState(null); // {ok, text}
  const [gcpLoading, setGcpLoading] = useState(false);
  const [llmPrompt, setLlmPrompt] = useState("");
  const [llmModel, setLlmModel] = useState("");
  const [llmResponse, setLlmResponse] = useState(null);
  const [llmLoading, setLlmLoading] = useState(false);

  return {
    debugMode,
    setDebugMode,
    mockToday,
    setMockToday,
    debugResolutionOverride,
    setDebugResolutionOverride,
    debugPremiseOverride,
    setDebugPremiseOverride,
    debugForceGenerating,
    setDebugForceGenerating,
    clearDebugOverrides,
    debugForceAlertBadge,
    setDebugForceAlertBadge,
    debugPanelOpen,
    setDebugPanelOpen,
    gcpResponse,
    setGcpResponse,
    gcpLoading,
    setGcpLoading,
    llmPrompt,
    setLlmPrompt,
    llmModel,
    setLlmModel,
    llmResponse,
    setLlmResponse,
    llmLoading,
    setLlmLoading,
  };
}
